<template>
  <div class="export-format-modal">
    <!-- Bouton déclencheur -->
    <span @click.prevent="open">
      <slot name="button" />
    </span>

    <!-- Modal -->
    <div
      class="export-modal"
      :class="{ 'is-visible': isOpen }"
      @click.self="close"
    >
      <div class="export-modal__inner">
        <button class="export-modal__close" @click.prevent="close">
          <SvgIcon name="cross" />
        </button>

        <div class="export-modal__heading">
          <TextTitle tag="h2" size="m">{{ $t('export.modal.title') }}</TextTitle>
          <TextSimple>{{ $t('export.modal.subtitle') }}</TextSimple>
        </div>

        <div class="export-modal__options">
          <label class="export-option" :class="{ 'is-selected': selectedFormat === 'csv' }">
            <input
              type="radio"
              name="exportFormat"
              value="csv"
              v-model="selectedFormat"
            />
            <span class="export-option__radio"></span>
            <span class="export-option__content">
              <span class="export-option__label">{{ $t('export.modal.csv_label') }}</span>
              <span class="export-option__description">{{ $t('export.modal.csv_description') }}</span>
            </span>
          </label>

          <label class="export-option" :class="{ 'is-selected': selectedFormat === 'xlsx' }">
            <input
              type="radio"
              name="exportFormat"
              value="xlsx"
              v-model="selectedFormat"
            />
            <span class="export-option__radio"></span>
            <span class="export-option__content">
              <span class="export-option__label">{{ $t('export.modal.xlsx_label') }}</span>
              <span class="export-option__description">{{ $t('export.modal.xlsx_description') }}</span>
            </span>
          </label>
        </div>

        <div class="export-modal__bottom">
          <Button color="grey" size="m" @click.native.prevent="close">
            {{ $t('global.cancel') }}
          </Button>
          <Button
            color="black"
            size="m"
            :loading="loading"
            @click.native.prevent="confirmExport"
          >
            {{ $t('export.modal.confirm') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextTitle from '@/components/ui/text/TextTitle';
import TextSimple from '@/components/ui/text/TextSimple';
import SvgIcon from '@/components/ui/svg/SvgIcon';
import Button from '@/components/ui/button/Button';

export default {
  name: 'ExportFormatModal',
  components: { TextTitle, TextSimple, SvgIcon, Button },
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      selectedFormat: 'csv' // CSV par défaut
    };
  },
  methods: {
    open() {
      document.body.classList.add('no-scroll');
      this.isOpen = true;
      this.selectedFormat = 'csv'; // Reset au format par défaut
    },
    close() {
      document.body.classList.remove('no-scroll');
      this.isOpen = false;
      this.$emit('close');
    },
    confirmExport() {
      this.$emit('export', this.selectedFormat);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.export-modal {
  position: fixed;
  z-index: 999;
  background-color: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(0.5rem);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--page-margin);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: background-color ease-out 0.3s, backdrop-filter ease-out 0.3s;

  &:not(.is-visible) {
    pointer-events: none;
    background-color: transparent;
    backdrop-filter: blur(0);

    .export-modal__inner {
      opacity: 0;
      transform: translateY(-10px);
    }
  }

  &__inner {
    width: 100%;
    max-width: 450px;
    background-color: #fff;
    border-radius: var(--border-radius-2);
    box-shadow: var(--box-shadow-2);
    position: relative;
    padding: 30px;
    transition: opacity ease-out 0.3s, transform ease-out 0.3s;
  }

  &__close {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 2;
    cursor: pointer;
    background: none;
    border: none;
    padding: 5px;

    svg {
      width: 10px;
      height: auto;
    }
  }

  &__heading {
    margin-bottom: 25px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 25px;
  }

  &__bottom {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    .button {
      margin: 0 !important;
    }
  }
}

.export-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #999;
  }

  &.is-selected {
    border-color: #000;
    background-color: #f9f9f9;
  }

  input[type="radio"] {
    display: none;
  }

  &__radio {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border: 2px solid #ccc;
    border-radius: 50%;
    position: relative;
    margin-top: 2px;
    transition: border-color 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0);
      width: 10px;
      height: 10px;
      background-color: #000;
      border-radius: 50%;
      transition: transform 0.2s ease;
    }
  }

  &.is-selected &__radio {
    border-color: #000;

    &::after {
      transform: translate(-50%, -50%) scale(1);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-weight: 500;
    font-size: 14px;
    color: #000;
  }

  &__description {
    font-size: 13px;
    color: #666;
  }
}
</style>
